<template>
  <div class="image-upload">
    <div class="columns is-multiline">
      <div
        v-if="!hideTitle"
        class="column is-full"
      >
        <div class="mb-5">
          <h3>{{ $t("images.upload.headline") }}</h3>
          <p>{{ $t("images.upload.description") }}</p>
        </div>
      </div>

      <div class="column is-full">
        <div class="upload__dropzone">
          <drop-zone
            ref="dropzone"
            v-bind="options"
            name="dropzone"
            accept="image/png, image/jpeg, image/bmp, image/gif"
            type="file"
            :loading="imagesLoading || isLoading"
            @change="onFilesDropped"
          >
            <!-- meta-info -->
            <div class="upload__meta mt-6">
              <div class="upload__meta-inner">
                <!-- accepted formats -->
                <div class="upload__formats">
                  <span class="icon is-large mr-3">
                    <i class="fa fa-image"></i>
                  </span>
                  <div>
                    <div class="text-bold">
                      {{ $t("images.upload.dropZone.images.headline") }}
                    </div>
                    <div v-if="settings">
                      <div>
                        <span>{{
                          $t("images.upload.dropZone.images.accepted")
                        }}</span>
                      </div>
                      <div>
                        {{
                          $t("images.upload.dropZone.images.size", {
                            size: filesizehumanreadable(
                              settings.upload.max_filesize_images,
                              lang
                            ),
                          })
                        }}
                      </div>
                      <div>
                        {{
                          $t("images.upload.dropZone.images.dimensions", {
                            minDimension,
                          })
                        }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- total -->
                <div :class="{ 'has-error': imagesToBig }">
                  <div class="upload__total">
                    <span class="icon is-large mr-3">
                      <i class="fa fa-cloud-upload"></i>
                    </span>
                    <div class="w-100">
                      <div class="text-bold">
                        {{ $t("images.upload.dropZone.images.total") }}
                      </div>
                      <div v-if="settings" class="text-nowrap">
                        {{
                          $t("images.upload.dropZone.images.maxSize", {
                            size: filesizehumanreadable(
                              settings.upload.max_postsize,
                              lang
                            ),
                          })
                        }}
                      </div>
                      <div class="d-flex align-items-center mt-0">
                        <div class="upload__progress-wrapper">
                          <div
                            class="upload__progress"
                            :class="{ 'upload__progress--danger': imagesToBig }"
                            :style="`width: ${imagesSize.percent}%`"
                          ></div>
                        </div>
                        <div
                          class="upload__size ml-3"
                          :class="{ 'is-danger': imagesToBig }"
                        >
                          {{ imagesSize.mb }} /
                          {{ bytesAsMb(settings.upload.max_postsize, 0) }} MB
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </drop-zone>
        </div>
      </div>

      <div v-if="imagesToBig" class="column is-full mt-3">
        <article class="message is-danger">
          <div class="message-body">
            <div class="is-danger">
              {{
                $t("form.errors.upload_size_images", {
                  MAX_SIZE: filesizehumanreadable(
                    settings.upload.max_postsize,
                    lang
                  ),
                })
              }}
            </div>
          </div>
        </article>
      </div>

      <template v-if="images.length > 0">
        <div class="column is-full-mobile is-half">
          <h3 class="mt-6">
            {{ $t("images.upload.uploadsHeadline") }}
          </h3>
          <p v-if="!isCampgroundImage">
            {{ $t("images.upload.uploadsDescription") }}
          </p>
        </div>

        <div class="column is-full">
          <form
            class="columns is-multiline"
            @submit.prevent="uploadImages"
            novalidate
          >
            <div
              v-for="(image, idx) in images"
              :key="idx"
              class="column is-full mb-4"
              :class="{
                'has-error': imageHasError(idx),
              }"
            >
              <image-input
                class="image-row"
                :value="images[idx]"
                :disabled="isLoading"
                :index="idx"
                :custom-errors="imageErrors[idx]"
                :valid-size="settings.upload.max_filesize_images"
                :valid-dimensions="settings.upload.min_imagesize"
                :hide-description="isCampgroundImage"
                @change="onImageChange($event, idx)"
                @removed="onImageRemove"
              ></image-input>
            </div>

            <div class="column is-clearfix" v-if="images.length > 0">
              <button
                type="submit"
                class="button is-primary is-pulled-right"
                :class="{
                  'is-loading': isLoading,
                  'is-disabled': isLoading,
                }"
              >
                {{ $t("generic.save") }}
              </button>
            </div>
          </form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import DropZone from '../dropzone/DropZone';
import ImageInput from './image-input';

import { Api } from '../../../api';
import { EventBus } from '../../../utils';
import { bytesAsMb, filesizehumanreadable } from '../../../utils/FileUtils';
import { AppModel } from '../../../models';

const createImagesFormData = (images, campsiteId) => {
    const formData = new FormData();

    formData.append('campsite', campsiteId);

    for(let i = 0; i < images.length; i += 1) {
        formData.append('images', images[i].file);
        formData.append('titles', images[i].description || '');
    }

    return formData;
};

const createCampgroundImagesFormData = (images) => {
    const formData = new FormData();

    for(let i = 0; i < images.length; i += 1) {
        formData.append('images', images[i].file);
    }

    return formData;
};

export default {
    name: 'image-upload',
    components: {
        DropZone,
        ImageInput
    },
    props: {
        hideTitle: {
            type: Boolean,
            default: false,
        },
        isSingleImage: {
            type: Boolean,
            default: false,
        },
        isCampgroundImage: {
            type: Boolean,
            default: false,
        },
        imageType: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            imageErrors: [],
            images: [],
            imagesLoading: false,
            isLoading: false,
            settings: AppModel.settings,
        };
    },
    computed: {
        options() {
            const options = {};

            if(!this.isSingleImage) {
                options.multiple = true;
            }

            return options;
        },
        lang() {
            return AppModel.lang;
        },
        imagesSize() {
            if(this.images.length === 0) return {bytes: 0, mb: 0, percent: 0};

            // calc total size
            const totalSize = this.images.reduce((total, image) => {
                return total + image.size;
            }, 0);

            return {
                bytes: totalSize || 0,
                mb: bytesAsMb(totalSize, 2),
                percent: ((totalSize / this.settings.upload.max_postsize) * 100).toFixed(2),
            };
        },
        imagesToBig() {
            return this.imagesSize.percent > 100;
        },
        minDimension() {
            return `${this.settings.upload.min_imagesize[0]}x${this.settings.upload.min_imagesize[1]}`;
        },
        formHasError() {
            const reducer = (accumulator, currentValue, currentIndex) => {
                if(accumulator) return accumulator;
                return this.imageHasError(currentIndex);
            };
            const hasError = this.images.reduce(reducer, false);
            return hasError || this.imagesToBig;
        }
    },
    methods: {
        imageHasError(idx) {
            return (this.imageErrors[idx] && this.imageErrors[idx].dimensions) || (this.imageErrors[idx] && this.imageErrors[idx].size);
        },

        bytesAsMb(value, digits) {
            return bytesAsMb(value, digits);
        },

        filesizehumanreadable(value, lang) {
            return filesizehumanreadable(value, lang);
        },

        imageHasValidDimensions(image) {
            // Logos (einzelne Campingplatz-Bilder) dürfen kleiner sein –
            // wir überspringen hier die globale Mindestgröße, damit der Upload nicht blockiert.
            if(this.isCampgroundImage && (this.isSingleImage || ['logo', 'logo_email', 'logo_booking'].includes(this.imageType))) {
                return true;
            }
            return image.width >= this.settings.upload.min_imagesize[0] && image.height >= this.settings.upload.min_imagesize[1];
        },

        imageHasValidSize(image) {
            return image.size < this.settings.upload.max_filesize_images;
        },

        onFilesDropped(files) {
            this.imagesLoading = true;
            const items = [...files];
            const images = [];

            items.forEach((image, i) => {
                const lengthToCheck = this.isSingleImage ? 1 : items.length;

                if(!this.isSingleImage || (this.isSingleImage && i === 0)) {
                    const reader = new FileReader();
                    reader.addEventListener(
                        'load',
                        event => {
                            const img = new Image();
                            img.onload = () => {
                                images.push({
                                    description: null,
                                    file: image,
                                    height: img.height,
                                    name: image.name,
                                    size: image.size,
                                    src: reader.result,
                                    width: img.width,
                                });

                                if(images.length === lengthToCheck) {
                                    this.onImagesLoaded(images);
                                }
                            };
                            img.src = event.target.result;
                        },
                        false,
                    );
                    reader.readAsDataURL(image);
                }
            });
        },

        onImagesLoaded(images) {
            this.imagesLoading = false;
            // save old length to scroll to first new image
            const oldLength = this.images.length;
            this.images = this.isSingleImage ? [...images] : [...this.images, ...images];

            // scroll after images have been rendered
            this.$nextTick(() => {
                // validate new images
                for(let i = oldLength; i < this.images.length; i++) {
                    this.$set(this.imageErrors, i, {
                        dimensions: !this.imageHasValidDimensions(this.images[i]),
                        size: !this.imageHasValidSize(this.images[i]),
                    });
                }

                this.scrollToError();

                // Auto-Upload für Campingplatz-Bilder (Logo, Logo-Email, Logo-Booking, Liste)
                // Hintergrund: Nutzer erwartet direkten Upload ohne zusätzlichen Klick auf „Speichern“.
                // Wir stoßen den Upload automatisch an, sobald ein gültiges Bild gedropped wurde.
                if(this.isCampgroundImage) {
                    // Nur auslösen, wenn keine Formularfehler vorliegen
                    if(!this.formHasError && this.images.length > 0) {
                        this.uploadImages();
                    }
                }
            });
        },

        onImageChange(data, idx) {
            this.$set(this.images, idx, data);
        },

        onImageRemove({ idx }) {
            // handle images
            const images = [...this.images];
            images.splice(idx, 1);
            this.images = images;

            // handle error object
            const imageErrors = [...this.imageErrors];
            imageErrors.splice(idx, 1);
            this.imageErrors = imageErrors;
        },

        scrollToError() {
            this.$nextTick(() => {
                const errorElement = this.$el.getElementsByClassName('has-error')[0];
                if(!errorElement) return;

                errorElement.scrollIntoView();
                // fix offset
                this.$nextTick(() => {
                    window.scrollTo({
                        top: window.scrollY - 60,
                    });
                });
            });
        },

        uploadImages() {
            if(this.formHasError) {
                this.scrollToError();
            } else {
                this.isLoading = true;

                const formData = this.isCampgroundImage
                    ? createCampgroundImagesFormData(this.images)
                    : createImagesFormData(this.images, this.$route.params.campsiteId);
                // scroll to drop-zone and show spinner
                this.$refs.dropzone.$el.scrollIntoView({ behavior: 'smooth' });

                if(this.isCampgroundImage) {
                    Api.campgroundImages.addImages(this.imageType, formData).then((response) => {
                        if(response.ok) {
                            setTimeout(() => {
                                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('images.notifications.upload.title')}</h3>`, direction: 'Up', body: this.$t('images.notifications.upload.body') });
                                this.$emit('uploaded');
                            }, 300);
                        } else {
                            this.scrollToError();
                        }
                    }).catch(() => {
                        this.scrollToError();
                    }).finally(() => {
                        this.isLoading = false;
                    });
                } else {
                    Api.campsiteImages.addImages(formData).then((response) => {
                        if(response.ok) {
                            setTimeout(() => {
                                this.$router.push({ name: this.$route.name, params: this.$route.params, query: { ...this.$route.query, tab: 0 } });
                                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('images.notifications.upload.title')}</h3>`, direction: 'Up', body: this.$t('images.notifications.upload.body') });
                            }, 300);
                        } else {
                            this.isLoading = false;
                            this.scrollToError();
                        }
                    }).catch(() => {
                        this.scrollToError();
                        this.isLoading = false;
                    });
                }
            }
        },
    },
};
</script>

<style lang="scss">
@import "image-upload.scss";
</style>



// WEBPACK FOOTER //
// src/views/components/campsite-images/ImageUpload.vue